/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import { LoggerModule } from "@vlinder-web/logger-module-react";

import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { DELETE_FEATURE, UPDATE_FEATURE } from "../graphql";
import { VApplication } from "../../../application";
import { CREATE_FEATURE } from "../graphql/createFeature.mutation";

const log: any = LoggerModule?.getInstance();

export interface FeatureFormData {
  name?: string;
  alias?: string;
  featurePosition?: string | number;
  isMandatory?: boolean;
  isVisible?: boolean;
  isLabel?: boolean;
  isSingleAttribute?: boolean;
  isCommon?: boolean;
  presentation?: string;
  sections?: string[];
  costumeType?: string;
  [key: string]: any;
}

export interface UpdateFeatureProps {
  formData: FeatureFormData;
  id: string;
  presentation?: string;
  section?: string;
  costumeType?: string;
}

export interface CreateFeatureProps extends FeatureFormData {
  formData?: FeatureFormData;
  id?: string;
}

export interface DeleteFeatureProps {
  id: string;
}

const FEATURE_FORM_FIELDS = [
  "name",
  "alias",
  "isLabel",
  "featurePosition",
  "isCommon",
  "isMandatory",
  "isVisible",
  "isSingleAttribute",
  "sections",
];

const FEATURE_PARENT_FIELDS = ["presentation", "costumeType"];

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

const trimIfString = (value: any) => {
  return typeof value === "string" ? value.trim() : value;
};
const toIntOrSkip = (value: any) => {
  if (value === "" || value === null || value === undefined) {
    return undefined;
  }

  const num = Number(value);

  if (Number.isNaN(num)) {
    return undefined;
  }

  return Math.trunc(num);
};
const buildAllowedPayload = (args: any) => {
  const source = {
    ...(args?.formData ?? args ?? {}),
  };

  FEATURE_PARENT_FIELDS.forEach((field) => {
    if (
      (source[field] === undefined ||
        source[field] === null ||
        source[field] === "") &&
      args?.[field] !== undefined
    ) {
      source[field] = args[field];
    }
  });

  const allowedFields = [...FEATURE_FORM_FIELDS, ...FEATURE_PARENT_FIELDS];

  return allowedFields.reduce((payload: any, field) => {
    if (!Object.prototype.hasOwnProperty.call(source, field)) return payload;

    let value = source[field];

    if (field === "featurePosition") {
      // value = toIntOrSkip(value);

      if (value === undefined) {
        return payload;
      }
    }
    if (FEATURE_PARENT_FIELDS.includes(field)) {
      if (value !== undefined && value !== null && value !== "") {
        payload[field] = value;
      }

      return payload;
    }
    if (field === "sections") {
      const sectionIds = Array.isArray(value)
        ? value.filter(Boolean)
        : value
        ? [value]
        : [];

      if (sectionIds.length > 0) {
        payload.section = sectionIds;
      }

      return payload;
    }
    if (value !== undefined) {
      payload[field] = field === "name" ? trimIfString(value) : value;
    }

    return payload;
  }, {});
};

export interface IFeatureMethods extends IPluginMethodMap {
  updateFeature(args: any): Promise<any>;
  createFeature(args: any): Promise<any>;
  deleteFeature(args: any): Promise<any>;
}

export class FeatureService implements IAgentPlugin {
  readonly methods: IFeatureMethods;

  constructor() {
    this.methods = {
      updateFeature: this.updateFeature.bind(this),
      createFeature: this.createFeature.bind(this),
      deleteFeature: this.deleteFeature.bind(this),
    };
  }

  async createFeature(args: CreateFeatureProps) {
    log?.blue?.(`createFeature server component:`, args);
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");

    try {
      if (client) {
        const data = buildAllowedPayload(args);

        const res = await client.mutate({
          mutation: CREATE_FEATURE,
          variables: {
            data,
          },
          fetchPolicy: "no-cache",
        });

        if (res?.data) {
          return {
            ...res?.data?.createCostumeFeature,
          };
        }
      }
    } catch (err: any) {
      let _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);
      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }

      throw err;
    }
  }

  async updateFeature(args: UpdateFeatureProps) {
    log?.blue?.(`updateFeature server component:`, args);
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");

    try {
      if (client) {
        const update = {
          ...buildAllowedPayload(args),
          isCommon: true,
        };

        const res = await client.mutate({
          mutation: UPDATE_FEATURE,
          variables: {
            filter: {
              _id: args?.id,
            },
            update,
          },
          fetchPolicy: "no-cache",
        });

        if (res?.data) {
          return {
            ...res?.data?.updateCostumeFeature,
          };
        }
      }
    } catch (err: any) {
      let _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);
      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }

      throw err;
    }
  }

  async deleteFeature(args: DeleteFeatureProps) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    try {
      if (client) {
        const res = await client.mutate({
          mutation: DELETE_FEATURE,
          variables: {
            filter: {
              _id: args?.id,
            },
          },
          fetchPolicy: "no-cache",
        });
        if (res?.data) {
          return {
            ...res?.data,
          };
        }
      }
    } catch (err: any) {
      let _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);
      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }

      throw err;
    }
  }
}
